/**
 * Auth Storage Utility
 *
 * Provides a centralised, per-tab session store for authentication data.
 *
 * ## Why sessionStorage instead of localStorage?
 *
 * | Concern | localStorage | sessionStorage |
 * |---------|-------------|----------------|
 * | Shared across tabs | ✅ yes — breaks multi-tab tenant switching | ❌ per-tab |
 * | Survives refresh | ✅ | ✅ |
 * | Survives tab close | ✅ | ❌ (desired for security) |
 * | XSS access to tokens | ✅ (bad) | N/A — tokens now in httpOnly cookies |
 *
 * JWT tokens (access + refresh) are **no longer** stored client-side.
 * They live in httpOnly cookies set by the backend. Frontend sends
 * `credentials: 'include'` on every request so the browser attaches
 * the cookie automatically.
 *
 * Only non-sensitive session metadata is stored here:
 *  - user profile (name, email, tenantId, role)
 *  - entitlement IDs
 *  - isAdmin flag
 *  - tenant onboarding status
 *  - selectedTenantId (for act-as-tenant)
 *
 * @see GitHub issue #24 — "Move JWT into a cookie"
 */

// ─── Storage keys ───────────────────────────────────────────────────
const KEYS = {
  USER: "user",
  ENTITLEMENTS: "entitlements",
  IS_ADMIN: "isAdmin",
  TENANT_ONBOARDING_COMPLETED: "tenantOnboardingCompleted",
  SELECTED_TENANT_ID: "selectedTenantId",
  /** Access token — fallback for backends that don't yet set httpOnly cookies */
  ACCESS_TOKEN: "access_token",
  /** Refresh token — fallback for backends that don't yet set httpOnly cookies */
  REFRESH_TOKEN: "refresh_token",
  /** ISO timestamp when the access token expires (used for proactive refresh) */
  TOKEN_EXPIRES_AT: "token_expires_at",
  /** Lightweight flag in localStorage so other tabs can detect login/logout */
  AUTH_EVENT: "auth_event",
} as const;

// ─── Types ──────────────────────────────────────────────────────────
export interface StoredUser {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  tenantId: number;
  companyId?: number;
  companyName?: string;
  displayName?: string;
  role?: string;
  isActive?: boolean;
  tenantOnboardingCompleted?: boolean;
}

// ─── Safe wrappers for browser APIs ─────────────────────────────────
// Firefox throws SecurityError (DOMException 18) in Enhanced Tracking Protection
// or restricted contexts when accessing sessionStorage/localStorage/location.

function safeGetItem(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(storage: Storage, key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch {
    // Storage access blocked — degrade silently
  }
}

function safeRemoveItem(storage: Storage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    // Storage access blocked — degrade silently
  }
}

/**
 * Safely assign window.location. Catches SecurityError in Firefox restricted contexts.
 * Exported for use by AuthContext cross-tab logout handler.
 */
export function safeNavigate(url: string): void {
  try {
    window.location.href = url;
  } catch {
    try {
      window.location.replace(url);
    } catch {
      // Completely blocked — nothing we can do
      console.warn("[auth] Navigation blocked by browser security policy");
    }
  }
}

// ─── Session helpers ────────────────────────────────────────────────

/** Save all auth-related data after login / refresh */
export function persistSession(data: {
  user: StoredUser;
  entitlementIds: number[];
  isAdmin: boolean;
  tenantOnboardingCompleted: boolean;
  accessToken?: string;
  refreshToken?: string;
  expiresAt?: string;
}): void {
  safeSetItem(sessionStorage, KEYS.USER, JSON.stringify(data.user));
  safeSetItem(
    sessionStorage,
    KEYS.ENTITLEMENTS,
    JSON.stringify(data.entitlementIds),
  );
  safeSetItem(sessionStorage, KEYS.IS_ADMIN, JSON.stringify(data.isAdmin));
  safeSetItem(
    sessionStorage,
    KEYS.TENANT_ONBOARDING_COMPLETED,
    JSON.stringify(data.tenantOnboardingCompleted),
  );

  // Store tokens in sessionStorage as fallback (per-tab, cleared on tab close)
  if (data.accessToken) {
    safeSetItem(sessionStorage, KEYS.ACCESS_TOKEN, data.accessToken);
  }
  if (data.refreshToken) {
    safeSetItem(sessionStorage, KEYS.REFRESH_TOKEN, data.refreshToken);
  }
  if (data.expiresAt) {
    safeSetItem(sessionStorage, KEYS.TOKEN_EXPIRES_AT, data.expiresAt);
  }

  // Broadcast login event to other tabs via localStorage
  // (value doesn't matter — we just need the storage event to fire)
  safeSetItem(localStorage, KEYS.AUTH_EVENT, `login:${Date.now()}`);
}

/** Restore session data (e.g. on page refresh within the same tab) */
export function restoreSession(): {
  user: StoredUser | null;
  entitlementIds: number[];
  isAdmin: boolean;
  tenantOnboardingCompleted: boolean;
} {
  const userStr = safeGetItem(sessionStorage, KEYS.USER);
  const entStr = safeGetItem(sessionStorage, KEYS.ENTITLEMENTS);
  const adminStr = safeGetItem(sessionStorage, KEYS.IS_ADMIN);
  const onboardStr = safeGetItem(
    sessionStorage,
    KEYS.TENANT_ONBOARDING_COMPLETED,
  );

  return {
    user: userStr ? (JSON.parse(userStr) as StoredUser) : null,
    entitlementIds: entStr ? (JSON.parse(entStr) as number[]) : [],
    isAdmin: adminStr ? (JSON.parse(adminStr) as boolean) : false,
    tenantOnboardingCompleted: onboardStr
      ? (JSON.parse(onboardStr) as boolean)
      : true,
  };
}

/** Clear all session data (on logout) */
export function clearSession(): void {
  safeRemoveItem(sessionStorage, KEYS.USER);
  safeRemoveItem(sessionStorage, KEYS.ENTITLEMENTS);
  safeRemoveItem(sessionStorage, KEYS.IS_ADMIN);
  safeRemoveItem(sessionStorage, KEYS.TENANT_ONBOARDING_COMPLETED);
  safeRemoveItem(sessionStorage, KEYS.SELECTED_TENANT_ID);
  safeRemoveItem(sessionStorage, KEYS.ACCESS_TOKEN);
  safeRemoveItem(sessionStorage, KEYS.REFRESH_TOKEN);
  safeRemoveItem(sessionStorage, KEYS.TOKEN_EXPIRES_AT);

  // Broadcast logout event to other tabs
  safeSetItem(localStorage, KEYS.AUTH_EVENT, `logout:${Date.now()}`);
}

/** Update just the user object (e.g. after onboarding completes) */
export function updateSessionUser(user: StoredUser): void {
  safeSetItem(sessionStorage, KEYS.USER, JSON.stringify(user));
}

/** Update onboarding status */
export function updateOnboardingInSession(completed: boolean): void {
  safeSetItem(
    sessionStorage,
    KEYS.TENANT_ONBOARDING_COMPLETED,
    JSON.stringify(completed),
  );
}

// ─── Token helpers (fallback for non-cookie backends) ───────────────

/** Get the access token from sessionStorage (fallback auth) */
export function getAccessToken(): string | null {
  return safeGetItem(sessionStorage, KEYS.ACCESS_TOKEN);
}

/** Get the refresh token from sessionStorage (fallback auth) */
export function getRefreshToken(): string | null {
  return safeGetItem(sessionStorage, KEYS.REFRESH_TOKEN);
}

/** Store a new access token (e.g. after refresh) */
export function setAccessToken(token: string): void {
  safeSetItem(sessionStorage, KEYS.ACCESS_TOKEN, token);
}

/** Get the token expiry timestamp from sessionStorage */
export function getTokenExpiresAt(): string | null {
  return safeGetItem(sessionStorage, KEYS.TOKEN_EXPIRES_AT);
}

/** Store the token expiry timestamp */
export function setTokenExpiresAt(expiresAt: string): void {
  safeSetItem(sessionStorage, KEYS.TOKEN_EXPIRES_AT, expiresAt);
}

/**
 * Return an object with the Authorization header if a token exists.
 * Designed to be spread into a headers object:
 *
 *   headers: { "Content-Type": "application/json", ...getAuthHeaders() }
 */
export function getAuthHeaders(): Record<string, string> {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ─── Tenant selection (per-tab) ─────────────────────────────────────

export function getSelectedTenantId(): number | null {
  const stored = safeGetItem(sessionStorage, KEYS.SELECTED_TENANT_ID);
  if (stored === null || stored === "null") return null;
  const parsed = parseInt(stored, 10);
  return isNaN(parsed) ? null : parsed;
}

export function setSelectedTenantIdInStorage(tenantId: number | null): void {
  safeSetItem(
    sessionStorage,
    KEYS.SELECTED_TENANT_ID,
    tenantId === null ? "null" : tenantId.toString(),
  );
}

// ─── Cross-tab auth event listener ──────────────────────────────────

/**
 * Listen for auth changes broadcast from other tabs.
 * When another tab logs out, this tab should also clear its session.
 */
export function onCrossTabAuthEvent(
  callback: (event: "login" | "logout") => void,
): () => void {
  const handler = (e: StorageEvent) => {
    if (e.key !== KEYS.AUTH_EVENT || !e.newValue) return;
    const type = e.newValue.startsWith("logout") ? "logout" : "login";
    callback(type);
  };

  window.addEventListener("storage", handler);
  return () => window.removeEventListener("storage", handler);
}

// ─── Legacy cleanup ─────────────────────────────────────────────────

/**
 * One-time migration: remove any token data left in localStorage from
 * the old implementation. Called once on app start.
 */
export function cleanupLegacyStorage(): void {
  const legacyKeys = [
    "token",
    "refreshToken",
    "user",
    "entitlements",
    "isAdmin",
    "tenantOnboardingCompleted",
    "tokenStoredAt",
    "selectedTenantId",
  ];

  for (const key of legacyKeys) {
    safeRemoveItem(localStorage, key);
  }
}
